'use client';

import { c as _c } from "react/compiler-runtime";
import './feature-article-card.scss';
import Link from 'next/link';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@sola/ark-core/atoms/badge/badge.tsx';
import { Prominence, Sizes, Text, Variants } from '@sola/ark-core/atoms/text/text.tsx';
import { SearchResultProps } from '@sola/skippy-search-sdk/lib/schema.ts';
import { trackClick } from '@sola/tracking/components/datalayer.tsx';
import { titleCase } from '@sola/utils/string.ts';
import { Image } from '../../atoms/image/image.tsx';
import { FeatureBase } from '../feature-base/feature-base.tsx';
const DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  month: 'long',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC'
} as const;
type FeaturedArticleCardProps = SearchResultProps & {
  seoLevel: number;
};
export const FeaturedArticleCard = t0 => {
  const $ = _c(46);
  let article;
  let seoLevel;
  if ($[0] !== t0) {
    ({
      seoLevel,
      ...article
    } = t0);
    $[0] = t0;
    $[1] = article;
    $[2] = seoLevel;
  } else {
    article = $[1];
    seoLevel = $[2];
  }
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["articles", "geo"];
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    t
  } = useTranslation(t1);
  const {
    title,
    summary
  } = article;
  const href = article.path || "#";
  let t2;
  bb0: {
    if (!article.publishDate) {
      t2 = null;
      break bb0;
    }
    let t3;
    if ($[4] !== article.publishDate) {
      t3 = new Date(article.publishDate).toLocaleDateString("en-US", DATE_FORMAT_OPTIONS);
      $[4] = article.publishDate;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    t2 = t3;
  }
  const publishedDate = t2;
  const firstContributor = article.contributors?.[0] || null;
  const hasTags = !!(article.taxonomyArticleType || article.taxonomyProgramCountry?.length || article.taxonomyArticleTheme?.length || article.taxonomyTopic?.length);
  const isClickable = !!href && href !== "#";
  let t3;
  if ($[6] !== href || $[7] !== isClickable || $[8] !== title) {
    t3 = isClickable ? {
      Comp: Link as React.ElementType,
      CompProps: {
        href,
        onClick: () => {
          trackClick(title || "Unknown", href, "FeaturedArticleCard", undefined, "Link");
        }
      }
    } : {
      Comp: "div" as React.ElementType,
      CompProps: {}
    };
    $[6] = href;
    $[7] = isClickable;
    $[8] = title;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const {
    Comp,
    CompProps
  } = t3;
  const t4 = article.media?.image;
  let t5;
  if ($[10] !== t4) {
    t5 = <Image image={t4} className="FeaturedArticleCard__image" hasPlaceholder={true} />;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="FeaturedArticleCard__media-overlay" />;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t5) {
    t7 = <div className="FeaturedArticleCard__media">{t5}{t6}</div>;
    $[13] = t5;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== summary) {
    t8 = <p>{summary}</p>;
    $[15] = summary;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== seoLevel || $[18] !== t8 || $[19] !== title) {
    t9 = <FeatureBase seoLevel={seoLevel} title={title} body={t8} className="max-w-md @lg:pr-roomy-lg" inline={true} />;
    $[17] = seoLevel;
    $[18] = t8;
    $[19] = title;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== firstContributor || $[22] !== t) {
    t10 = firstContributor && <div className="flex flex-col gap-snug-lg"><Text variant={Variants.Label} size={Sizes.Medium} prominence={Prominence.Potent}>{t("articles:writtenBy")}</Text><div className="flex flex-row items-center gap-snup-lg">{firstContributor.image?.url && <div className="pr-comfy-xs"><Image image={{
            url: firstContributor.image?.url,
            alt: firstContributor.image?.alt || firstContributor.name
          }} className="FeaturedArticleCard__avatar w-8 h-8 rounded-full object-cover" /></div>}<Text variant={Variants.Label} size={Sizes.Medium} prominence={Prominence.Moderate}>{firstContributor.name}</Text></div></div>;
    $[21] = firstContributor;
    $[22] = t;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== publishedDate || $[25] !== t) {
    t11 = publishedDate && <div className="flex flex-col gap-snug-lg"><Text variant={Variants.Label} size={Sizes.Medium} prominence={Prominence.Potent}>{t("articles:publishedOn")}</Text><div className="mt-snug-lg"><Text variant={Variants.Label} size={Sizes.Medium} prominence={Prominence.Moderate}>{publishedDate}</Text></div></div>;
    $[24] = publishedDate;
    $[25] = t;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== article.taxonomyArticleTheme || $[28] !== article.taxonomyArticleType || $[29] !== article.taxonomyProgramCountry || $[30] !== article.taxonomyTopic || $[31] !== hasTags || $[32] !== t) {
    t12 = hasTags && <div className="flex flex-col gap-snug-lg sm:ml-auto text-left"><Text variant={Variants.Label} size={Sizes.Medium} prominence={Prominence.Potent}>{t("articles:tags")}</Text><div className="flex flex-wrap justify-end gap-snug-xl mt-snug-lg">{article.taxonomyArticleType?.[0] && <Badge key="type" label={titleCase(article.taxonomyArticleType)} />}{article.taxonomyProgramCountry?.[0] && <Badge key="country" label={titleCase(t(`geo:countries.${article.taxonomyProgramCountry[0]}.nameS`))} />}{article.taxonomyArticleTheme?.[0] && <Badge key="theme" label={titleCase(article.taxonomyArticleTheme[0])} />}{article.taxonomyTopic?.[0] && <Badge key="topic" label={titleCase(article.taxonomyTopic[0])} />}</div></div>;
    $[27] = article.taxonomyArticleTheme;
    $[28] = article.taxonomyArticleType;
    $[29] = article.taxonomyProgramCountry;
    $[30] = article.taxonomyTopic;
    $[31] = hasTags;
    $[32] = t;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== t10 || $[35] !== t11 || $[36] !== t12) {
    t13 = <div className="FeaturedArticleCard__meta flex items-start justify-between gap-comfy-xl flex-wrap pt-comfy-lg">{t10}{t11}{t12}</div>;
    $[34] = t10;
    $[35] = t11;
    $[36] = t12;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  let t14;
  if ($[38] !== t13 || $[39] !== t9) {
    t14 = <div className="FeaturedArticleCard__content">{t9}{t13}</div>;
    $[38] = t13;
    $[39] = t9;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] !== Comp || $[42] !== CompProps || $[43] !== t14 || $[44] !== t7) {
    t15 = <Comp {...CompProps} className="FeaturedArticleCard w-full max-w-xl mx-auto aspect-ratio-1 @md:aspect-ratio-16-9 @lg:aspect-ratio-21-9">{t7}{t14}</Comp>;
    $[41] = Comp;
    $[42] = CompProps;
    $[43] = t14;
    $[44] = t7;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  return t15;
};